// Libs
import classNames from 'classnames/bind';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
// Components, Layouts, Pages
import { BaseInput, BaseSelect } from '~/components/common';
// Others
import { IOptionProductItem } from '~/utils/interface/purchaseOrder';
import { IInventoryProduct, IParamInventoryProduct } from '~/utils/interface/inventory';
import {
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  DOLLAR,
  EMPTY_STRING,
  REG_EXP,
} from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getListInventoryProduct } from '~/thunks/inventory/inventoryThunk';
import { IFormInvoice } from '~/utils/interface/invoice';
import { IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import { formatInputCurrency } from '~/utils/helper/common';
// Styles, images, icons
import styles from './ItemInvoice.module.scss';
import { icons } from '~/assets';

type Props = {
  disabled: boolean;
  index: number;
  onRemove: (index: number) => void;
};

const cx = classNames.bind(styles);

const ItemInvoice = (props: Props) => {
  //#region Destructuring Props
  const { disabled, index, onRemove } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/invoice', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<IFormInvoice>();

  const { deleteItemIds } = watch();

  const item = useWatch({
    control,
    name: `invoiceItems.${index}`,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleCalcTotal = (newItem: {
    quantity?: string;
    price?: string;
    laborCost?: string;
    discountRate?: string;
  }) => {
    const newTotal =
      Number((newItem?.quantity ?? item?.quantity) || 0) * Number((newItem?.price ?? item?.price) || 0) +
      Number((newItem?.laborCost ?? item?.laborCost) || 0) -
      Number((newItem?.discountRate ?? item?.discountRate) || 0);

    setValue(`invoiceItems.${index}.total`, Math.max(newTotal, 0).toFixed(2).toString(), {
      shouldValidate: true,
    });
  };

  const fetchOptionsListProductItem = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IOptionProductItem>> => {
      const { page, searchKey } = payload;
      const payloadParams: IParamInventoryProduct = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        isHasVariant: true,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListInventoryProduct(payloadParams)).unwrap();

        const { responses: listInventory, pagination } = resp?.data;

        const formatOptions: IOptionProductItem[] = handleConvertOptionAddOn(listInventory);

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const handleConvertOptionAddOn = (data: IInventoryProduct[]): IOptionProductItem[] => {
    const options: IOptionProductItem[] = data.map((item) => ({
      value: `${item.id}_${item.variantId}`,
      productId: item.id,
      label: `${item.name} ${item?.variantName ? ` - ${item?.variantName}` : EMPTY_STRING}`,
      price: item.rentalPrice,
      variantId: item.variantId,
      name: item?.name,
      variantName: item?.variantName,
    }));

    return options;
  };

  const handleRemoveItem = () => {
    if (item?.idDetail) {
      setValue('deleteItemIds', [...(deleteItemIds || []), item?.idDetail]);
    }
    onRemove(index);
  };
  //#endregion Handle Function

  return (
    <Stack direction={'row'} key={item?.id ?? index} spacing={2} className={cx('itemInvoice')}>
      <Controller
        name={`invoiceItems.${index}.productId`}
        control={control}
        render={({ field: { onChange, value } }) => (
          <Box minWidth={200}>
            <BaseSelect
              isSearch
              gap={8}
              height={37}
              required
              disabled={disabled}
              width={200}
              value={value}
              label={t('invoice_detail_table_name')}
              callApiFn={fetchOptionsListProductItem}
              itemNameDefault={`${item?.name} - ${item?.variantName}`}
              type='loadMore'
              onChange={(selected) => {
                onChange(selected?.value);
                setValue(`invoiceItems.${index}.inventoryId`, selected?.productId || EMPTY_STRING, {
                  shouldValidate: true,
                });
                setValue(`invoiceItems.${index}.name`, selected?.name || EMPTY_STRING, {
                  shouldValidate: true,
                });
                setValue(`invoiceItems.${index}.variantId`, selected?.variantId || EMPTY_STRING, {
                  shouldValidate: true,
                });
                setValue(`invoiceItems.${index}.variantName`, selected?.variantName || EMPTY_STRING, {
                  shouldValidate: true,
                });
                setValue(`invoiceItems.${index}.price`, selected?.price || EMPTY_STRING, {
                  shouldValidate: true,
                });
                handleCalcTotal({ price: selected?.price });
              }}
              errorMessage={errors.invoiceItems?.[index]?.productId?.message}
            />
          </Box>
        )}
      />

      <Controller
        control={control}
        name={`invoiceItems.${index}.discountRate`}
        render={({ field: { onChange, value } }) => (
          <BaseInput
            height={37}
            startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
            label={t('invoice_form_title_discount')}
            placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
            value={value}
            disabled={disabled}
            onChange={(e) => {
              onChange(formatInputCurrency(e.target.value));
              handleCalcTotal({ discountRate: e.target.value });
            }}
          />
        )}
      />

      <Controller
        control={control}
        name={`invoiceItems.${index}.price`}
        render={({ field: { onChange, value } }) => (
          <BaseInput
            height={37}
            disabled={disabled}
            required
            label={t('invoice_detail_table_rental_price')}
            startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
            placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
            value={value}
            onChange={(e) => {
              onChange(formatInputCurrency(e.target.value));
              handleCalcTotal({ price: e.target.value });
            }}
            errorMessage={errors.invoiceItems?.[index]?.price?.message}
          />
        )}
      />

      <Controller
        control={control}
        name={`invoiceItems.${index}.laborCost`}
        render={({ field: { onChange, value } }) => (
          <BaseInput
            disabled={disabled}
            height={37}
            label={t('invoice_detail_table_labor_cost')}
            startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
            placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
            value={value}
            onChange={(e) => {
              onChange(formatInputCurrency(e.target.value));
              handleCalcTotal({ laborCost: e.target.value });
            }}
          />
        )}
      />

      <Controller
        control={control}
        name={`invoiceItems.${index}.quantity`}
        render={({ field: { onChange, value } }) => (
          <BaseInput
            height={37}
            disabled={disabled}
            required
            label={t('invoice_detail_table_quantity')}
            placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
            value={value}
            onChange={(e) => {
              const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
              const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
              onChange(numericValue);
              handleCalcTotal({ quantity: e.target.value });
            }}
          />
        )}
      />

      <Controller
        control={control}
        name={`invoiceItems.${index}.total`}
        render={({ field: { onChange, value } }) => (
          <BaseInput
            height={37}
            disabled={disabled}
            required
            label={t('invoice_detail_table_total')}
            placeholder={t('common_text_placeholder_number', { ns: 'common/base' })}
            startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
            value={value}
            onChange={(e) => {
              onChange(formatInputCurrency(e.target.value));
            }}
            errorMessage={errors.invoiceItems?.[index]?.total?.message}
          />
        )}
      />

      <Box>
        <IconButton
          className={cx('buttonDelete')}
          key={item?.id || index}
          disabled={disabled}
          onClick={() => {
            handleRemoveItem();
          }}
        >
          <img
            className={cx('iconDelete')}
            src={icons.commonIconTrash}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </Box>
    </Stack>
  );
};

export default ItemInvoice;
